/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, FC, useMemo, useState } from "react";
import debounce from "debounce";
import cn from "classnames";
import { Form, FormikConfig, FormikProvider, useFormik } from "formik";
import { getVisibleSteps } from "./utils/getVisibleSteps";
import { TotalAmountBlock } from "./TotalAmountBlock";
import { RenderSteps } from "./RenderSteps";
import { CALCULATOR_VALIDATION_SCHEMA } from "./constants/calculator-validation";
import { CalculatorField, CalculatorValues, LegalForm } from "./types";
import { useCalculatePriceMutation } from "src/redux/api";
import { mapCalculatorValuesToApi } from "./utils/mapCalculatorValuesToApi";
import { NotificationService } from "src/helpers/notifications";

const DEBOUCE_DELAY = 300; // ms

const INITIAL_VALUES: CalculatorValues = {
  [CalculatorField.legalForm]: null,
  [CalculatorField.taxationSystem]: null,
  [CalculatorField.singleTaxGroup]: null,
  [CalculatorField.vatPayer]: null,
  [CalculatorField.hiredWorkers]: null,
  [CalculatorField.workersCount]: null,
  [CalculatorField.exciseTaxPayer]: null,
};

export const CostCalculator: FC = () => {
  const [amount, setAmount] = useState<number>(null);
  const [calculatePrice, { isLoading }] = useCalculatePriceMutation();

  //debounced handlers
  const { handleSubmit, resetAmount } = useMemo(
    () => ({
      handleSubmit: debounce(async (value: CalculatorValues) => {
        try {
          const apiInput = mapCalculatorValuesToApi(value);
          const response = await calculatePrice(apiInput).unwrap();
          
          // Parse total_price from string to number
          const totalPrice = parseFloat(response.total_price);
          if (!isNaN(totalPrice)) {
            setAmount(totalPrice);
          } else {
            throw new Error("Невірний формат відповіді від сервера");
          }
        } catch (error: any) {
          console.error("Calculator API error:", error);
          const errorMessage = 
            error?.data?.detail || 
            error?.data?.message || 
            error?.message || 
            "Помилка при розрахунку вартості. Спробуйте ще раз.";
          NotificationService.error(errorMessage);
          setAmount(null);
        }
      }, DEBOUCE_DELAY),

      resetAmount: debounce(() => setAmount(null), DEBOUCE_DELAY),
    }),
    [calculatePrice]
  );

  const formikConfig: FormikConfig<CalculatorValues> = {
    initialValues: INITIAL_VALUES,
    validationSchema: CALCULATOR_VALIDATION_SCHEMA,
    onSubmit: handleSubmit,
  };
  const formik = useFormik<CalculatorValues>(formikConfig);

  const visibleSteps = getVisibleSteps(formik);
  const legalForm = formik.values[CalculatorField.legalForm];
  const taxationSystem = formik.values[CalculatorField.taxationSystem];

  useEffect(() => {
    const resetValues = { ...INITIAL_VALUES, legal_form: legalForm };
    if (formik.dirty) formik.setValues(resetValues, true);
  }, [legalForm]);

  // Auto-set 4 group when "Загальна" is selected for individual entrepreneur
  // Reset group when switching from "Загальна" to "Спрощена"
  useEffect(() => {
    if (legalForm === LegalForm.indEnt) {
      if (taxationSystem === "загальна") {
        if (formik.values[CalculatorField.singleTaxGroup] !== "4-група") {
          formik.setFieldValue(CalculatorField.singleTaxGroup, "4-група");
        }
      } else if (taxationSystem === "спрощена" && formik.values[CalculatorField.singleTaxGroup] === "4-група") {
        formik.setFieldValue(CalculatorField.singleTaxGroup, null);
      }
    }
  }, [legalForm, taxationSystem]);

  // При виборі "Так" на працівників — ставимо 1; при "Ні" — скидаємо
  useEffect(() => {
    const hired = formik.values[CalculatorField.hiredWorkers];
    if (hired === true) {
      formik.setFieldValue(CalculatorField.workersCount, 1);
    } else if (hired === false) {
      formik.setFieldValue(CalculatorField.workersCount, null);
    }
  }, [formik.values[CalculatorField.hiredWorkers]]);

  useEffect(() => {
    const isValid = formik.isValid && formik.dirty;
    if (isValid) formik.submitForm();
    else resetAmount();
  }, [formik.values, formik.errors]);

  return (
    <FormikProvider value={formik}>
      <Form
        className={cn("flex flex-col gap-4", {
          "animate-pulse pointer-events-none": isLoading,
        })}
      >
        <RenderSteps steps={visibleSteps} />
        <TotalAmountBlock amount={amount} />
      </Form>
    </FormikProvider>
  );
};
